/**
 * Outcome page components.
 *
 * Five compassionate, non-legal-advice outcome variants. Copy lives
 * here in TypeScript so an attorney reviewing copy can edit one
 * file end-to-end (the Phase 6 review will sign off on this and
 * potentially move it into a CMS later).
 *
 * Tone constraints (per the Phase 3 plan, non-negotiable):
 *   - We do NOT give legal advice.
 *   - We do NOT assert legal conclusions.
 *   - We DO surface "based on your answer" as the framing — never
 *     "you are ineligible" / "you cannot file".
 *   - Each ineligible/refer outcome shows the AILA "find an
 *     attorney" link.
 *
 * Each outcome page accepts:
 *   - `reason` (the router's stable id) for surfacing a one-line
 *     "why" without re-asking the user
 *   - `onContinue` for the eligible page (continues to /sign-up)
 *   - `onRescreen` for ALL pages (resets the flow back to
 *     `/screener/landing`)
 */
import styles from './ScreenerOutcomes.module.css';
import type { ScreenerOutcomeId } from './screenerConfig';
import { ScreenerTopBar } from './ScreenerTopBar';

const AILA_DIRECTORY_URL = 'https://www.ailalawyer.com/';

interface IBaseOutcomeProps {
  /** Stable router-emitted reason for the outcome. */
  reason: string;
  /** Re-screen — typically navigates back to the landing page. */
  onRescreen: () => void;
  /**
   * Back to the question whose answer fired this outcome, with that
   * answer cleared. Optional — undefined when the outcome was reached
   * without a preceding question (currently never happens). When
   * supplied the OutcomeShell renders a "← Back to that question"
   * link so the user can revise a single mistaken pick instead of
   * restarting from scratch via "Re-take the screener".
   */
  onBack?: () => void;
  /** Persistent "Sign in" affordance in the top bar — hidden when
   *  omitted. */
  onSignInClick?: () => void;
}

export interface IEligibleOutcomeProps extends IBaseOutcomeProps {
  onContinue: () => void;
}

export function EligibleOutcome({
  reason,
  onContinue,
  onRescreen,
  onBack,
  onSignInClick,
}: IEligibleOutcomeProps): JSX.Element {
  return (
    <OutcomeShell
      kicker="Looks like a good fit"
      bannerVariant="eligible"
      title="Your case looks straightforward."
      lede="Based on your answers, a marriage-based green card through Form I-485 looks like the right path. Nothing in your screener suggested an attorney is required up front."
      detail={
        <>
          <p>
            The next step is the full intake — about 100 questions covering
            your identity, immigration history, sponsor finances, and
            documents. We'll save what you've already told us so you don't
            re-answer.
          </p>
          <p>
            We'll also ask you to create a free account so you can save your
            progress and finish on a different device.
          </p>
        </>
      }
      reason={reason}
      onRescreen={onRescreen}
      onBack={onBack}
      onSignInClick={onSignInClick}
    >
      <button
        type="button"
        className={`${styles.outcome__cta_primary} ${styles['outcome__cta_primary--eligible']}`}
        onClick={onContinue}
      >
        Continue to full intake →
      </button>
    </OutcomeShell>
  );
}

export function IneligibleCriminalOutcome({
  reason,
  onRescreen,
  onBack,
  onSignInClick,
}: IBaseOutcomeProps): JSX.Element {
  return (
    <OutcomeShell
      kicker="An attorney should review your case"
      bannerVariant="ineligible"
      title="Based on your answer, this case usually requires attorney review."
      lede="Some answers in our screener flag complex issues — like trafficking-related grounds or terrorist-activity grounds — that we don't try to handle on a self-service tool. We're not saying you can't file. We're saying we'd rather not be the ones to file it for you."
      detail={
        <>
          <p>
            An immigration attorney can review the specific facts and tell you
            whether a waiver or other path is available. Many attorneys offer
            free initial consultations.
          </p>
        </>
      }
      reason={reason}
      onRescreen={onRescreen}
      onBack={onBack}
      onSignInClick={onSignInClick}
    >
      <a
        href={AILA_DIRECTORY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.outcome__cta_primary}
      >
        Find an immigration attorney (AILA directory) →
      </a>
    </OutcomeShell>
  );
}

export function IneligibleRemovalOutcome({
  reason,
  onRescreen,
  onBack,
  onSignInClick,
}: IBaseOutcomeProps): JSX.Element {
  return (
    <OutcomeShell
      kicker="Removal-history detected"
      bannerVariant="ineligible"
      title="A prior or active removal order changes the path."
      lede="If you've had a final removal order, you typically need to deal with the underlying order before USCIS can grant adjustment of status. There's a category of motion called a 'motion to reopen,' but whether it applies depends on the facts."
      detail={
        <>
          <p>
            We don't make that call. An immigration attorney who has seen your
            specific paperwork can look at whether the order is final, whether
            time has run, and what relief might be available.
          </p>
        </>
      }
      reason={reason}
      onRescreen={onRescreen}
      onBack={onBack}
      onSignInClick={onSignInClick}
    >
      <a
        href={AILA_DIRECTORY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.outcome__cta_primary}
      >
        Find an immigration attorney (AILA directory) →
      </a>
    </OutcomeShell>
  );
}

export function ReferAttorneyOutcome({
  reason,
  onRescreen,
  onBack,
  onSignInClick,
}: IBaseOutcomeProps): JSX.Element {
  return (
    <OutcomeShell
      kicker="Worth talking to an attorney first"
      bannerVariant="attorney"
      title="An attorney consultation is a good next step."
      lede="Your case has at least one signal — a prior arrest, a public-charge concern, an unlawful-presence question, or a similar issue — where waivers and exceptions exist but the right answer depends on facts we don't see. Many cases like yours do go forward; they just go forward with someone who has seen them before."
      detail={
        <>
          <p>
            We could try to walk you through the full intake anyway, but a 30-
            minute consultation with an attorney often saves weeks of guessing.
            Many attorneys offer free initial consultations.
          </p>
        </>
      }
      reason={reason}
      onRescreen={onRescreen}
      onBack={onBack}
      onSignInClick={onSignInClick}
    >
      <a
        href={AILA_DIRECTORY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.outcome__cta_primary}
      >
        Find an immigration attorney (AILA directory) →
      </a>
    </OutcomeShell>
  );
}

export function ReferDifferentFormOutcome({
  reason,
  onRescreen,
  onBack,
  onSignInClick,
}: IBaseOutcomeProps): JSX.Element {
  return (
    <OutcomeShell
      kicker="A different filing path fits better"
      bannerVariant="different_form"
      title="It looks like you're on a different path than what this tool covers."
      lede="Greenbroad helps people who are inside the United States and applying for a green card through marriage to a U.S. citizen. Based on your answers, the right next step for you is somewhere else."
      detail={
        <p>
          USCIS has a guide for every path. Below is a starting point; an
          immigration attorney can confirm which forms apply.
        </p>
      }
      reason={reason}
      onRescreen={onRescreen}
      onBack={onBack}
      onSignInClick={onSignInClick}
    >
      <a
        href="https://www.uscis.gov/green-card"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.outcome__cta_primary}
      >
        Browse USCIS green-card paths →
      </a>
      <a
        href={AILA_DIRECTORY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.outcome__cta_secondary}
      >
        Talk to an attorney
      </a>
    </OutcomeShell>
  );
}

/**
 * Shared layout. Each outcome tweaks the kicker, banner variant,
 * title, lede, detail, and primary CTA row; everything else
 * (chrome, why-line, re-screen link, disclaimer) is shared.
 */
function OutcomeShell({
  kicker,
  bannerVariant,
  title,
  lede,
  detail,
  reason,
  onRescreen,
  onBack,
  onSignInClick,
  children,
}: {
  kicker: string;
  bannerVariant: 'eligible' | 'attorney' | 'different_form' | 'ineligible';
  title: string;
  lede: string;
  detail: React.ReactNode;
  reason: string;
  onRescreen: () => void;
  onBack?: () => void;
  onSignInClick?: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const bannerClass = `${styles.outcome__banner} ${
    styles[`outcome__banner--${bannerVariant}` as keyof typeof styles]
  }`;

  return (
    <main
      id="main-content"
      className={styles.outcome}
      aria-labelledby="outcome-title"
    >
      <ScreenerTopBar
        metaLabel="Screener result"
        onSignInClick={onSignInClick}
      />
      <div className={styles.outcome__container}>
        <div className={styles.outcome__card}>
          <div className={bannerClass}>{kicker}</div>
          <div className={styles.outcome__body}>
            <h1 id="outcome-title" className={styles.outcome__title}>
              {title}
            </h1>
            <p className={styles.outcome__lede}>{lede}</p>
            <div className={styles.outcome__detail}>{detail}</div>
            <div className={styles.outcome__why}>
              <span className={styles.outcome__why_label}>Why we said this:</span>
              <span data-testid="outcome-reason">{humanReason(reason)}</span>
            </div>
            <div className={styles.outcome__cta_row}>{children}</div>
            <div className={styles.outcome__secondary_row}>
              {onBack ? (
                <button
                  type="button"
                  className={styles.outcome__back}
                  onClick={onBack}
                >
                  ← Back to that question (edit my answer)
                </button>
              ) : null}
              <button
                type="button"
                className={styles.outcome__rescreen}
                onClick={onRescreen}
              >
                Re-take the screener from the start
              </button>
            </div>
            <p className={styles.outcome__disclaimer}>
              This screener is informational only. It is not legal advice and
              does not create an attorney–client relationship. A licensed
              immigration attorney can review your facts and recommend a path.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Translate router reason ids into human-readable strings shown in the
 * "why we said this" callout. Keep the language defensive — never
 * assert a legal conclusion, only echo the user's answer in friendly
 * form.
 */
export function humanReason(reason: string): string {
  if (reason.startsWith('severe_harm:')) {
    return "you indicated participation in serious-harm activity in our final screener question.";
  }
  // Bundle reasons take the form
  // `immigration_history:<value>` or `criminal_public_charge:<value>`.
  // Dispatch on the suffix.
  if (reason.startsWith('immigration_history:')) {
    const value = reason.slice('immigration_history:'.length);
    switch (value) {
      case 'final_removal_order':
        return "you indicated a final removal, exclusion, or deportation order has been issued against you.";
      case 'reinstated_removal_order':
        return "you indicated a prior removal order has been reinstated.";
      case 'removal_proceedings':
        return "you indicated you are or have been in removal proceedings.";
      case 'unlawful_presence_over_one_year':
        return "you indicated more than one year of unlawful presence followed by departure and re-entry.";
      case 'misrepresentation':
        return "you indicated a prior misrepresentation on a U.S. immigration application.";
      default:
        return 'one of your immigration-history answers triggered an attorney-review path.';
    }
  }
  if (reason.startsWith('criminal_public_charge:')) {
    const value = reason.slice('criminal_public_charge:'.length);
    switch (value) {
      case 'drug_trafficking':
        return "you indicated illicit trafficking of a controlled substance.";
      case 'human_trafficking':
        return "you indicated human-trafficking-related history.";
      case 'terrorist_group_member':
        return "you indicated membership in a group that has engaged in terrorist activity.";
      case 'arrested_or_detained':
        return "you indicated a prior arrest, citation, charge, or detention.";
      case 'convicted':
        return "you indicated a prior plea or conviction.";
      case 'public_charge':
        return "you indicated receipt of a benefit on the public-charge list.";
      default:
        return 'one of your criminal or public-charge answers triggered an attorney-review path.';
    }
  }
  switch (reason) {
    case 'all_clean':
      return "your answers don't show any of the issues we screen for up front.";
    case 'filing_path_not_spouse_of_us_citizen':
      return "your filing path is not a marriage-based green card through a U.S.-citizen spouse.";
    case 'inside_united_states_no':
      return "you said you are not currently inside the United States.";
    case 'derivative_applicant':
      return "you said you are a derivative applicant rather than the primary applicant.";
    case 'asylee_or_refugee_path':
      return "you said you hold asylee or refugee status, which uses a different adjustment process.";
    case 'entered_without_inspection':
      return "you said you entered the U.S. without inspection.";
    case 'sponsor_is_lpr_not_usc':
      return "your sponsoring spouse is a lawful permanent resident, not a U.S. citizen.";
    case 'j1_two_year_unsatisfied':
      return "you said the J-1 two-year home-residence requirement has not been satisfied or waived.";
    default:
      return 'one of your answers triggered an attorney-review path in our screener.';
  }
}

/**
 * Map a `ScreenerOutcomeId` to the corresponding component for the
 * routing layer (Slice 6) to dispatch on.
 */
export const outcomeComponentById: Record<
  ScreenerOutcomeId,
  | typeof EligibleOutcome
  | typeof IneligibleCriminalOutcome
  | typeof IneligibleRemovalOutcome
  | typeof ReferAttorneyOutcome
  | typeof ReferDifferentFormOutcome
> = {
  eligible: EligibleOutcome,
  ineligible_criminal: IneligibleCriminalOutcome,
  ineligible_removal: IneligibleRemovalOutcome,
  refer_attorney: ReferAttorneyOutcome,
  refer_different_form: ReferDifferentFormOutcome,
};
